import React from 'react';
import { Award, Users, TrendingUp, Shield, ShieldCheck, Ruler, Zap } from 'lucide-react';
import { companyInfo } from '../mockData';
import { PageHero, SectionHeader, Reveal, TechCard, IconBox, Stat } from '../components/ui-kit';
import { CTA } from './Home';

const milestones = [
  { year: '2004', event: "Premiers chantiers de l'équipe fondatrice en électricité générale dans le Grand Ouest." },
  { year: '2024', event: `Création de TSE OUEST à ${companyInfo.city}, lancement des activités électricité & plâtrerie.` },
  { year: '2025', event: `Plus de ${companyInfo.experience} ans d'expérience cumulée au service des particuliers et des professionnels.` }
];

const values = [
  { icon: Award, title: 'Excellence', description: "Nous visons l'excellence dans chaque projet, avec des finitions impeccables et un respect strict des normes." },
  { icon: Users, title: "À l'écoute", description: 'Votre satisfaction est notre priorité. Nous sommes à votre écoute pour comprendre et réaliser vos projets.' },
  { icon: TrendingUp, title: 'Innovation', description: 'Nous restons à la pointe des nouvelles technologies et techniques pour vous offrir les meilleures solutions.' },
  { icon: Shield, title: 'Fiabilité', description: `Une entreprise de confiance, avec une équipe expérimentée de plus de ${companyInfo.experience} ans et des clients satisfaits.` }
];

const certs = [
  { icon: Zap, label: 'NF C 15-100', text: 'Installations électriques basse tension conformes.' },
  { icon: Ruler, label: 'DTU 25.41', text: 'Ouvrages en plaques de plâtre selon les règles de l\'art.' },
  { icon: ShieldCheck, label: 'Assurance décennale', text: 'Vos travaux couverts pendant 10 ans.' }
];

const About = () => (
  <div className="min-h-screen" data-testid="about-page">
    <PageHero
      testId="about-hero"
      eyebrow="TSE OUEST"
      title={<>Plus de {companyInfo.experience} ans d'expertise <span className="text-energy-gradient">à votre service.</span></>}
      subtitle="Une entreprise spécialisée en électricité générale et travaux de plâtrerie, installée à Angers et portée par une équipe de passionnés."
      image="/assets/tse-ouest-team-hero.png"
    />

    <section className="bg-[#F8FAFC] text-ink py-24 lg:py-32 circuit-grid-light" data-testid="about-story">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-14 items-start">
        <div className="lg:col-span-6">
          <SectionHeader eyebrow="Qui sommes-nous" title="Une entreprise angevine, une exigence d'ingénieur." />
          <Reveal delay={150} className="mt-8 space-y-5 text-slate-600 leading-relaxed">
            <p className="text-base md:text-lg text-slate-700">
              <strong className="text-ink">TSE OUEST</strong> est une entreprise spécialisée en électricité générale et travaux de plâtrerie, créée en {companyInfo.foundedYear} et installée à {companyInfo.city}.
            </p>
            <p>Notre équipe possède plus de {companyInfo.experience} ans d'expérience dans le secteur. Nous intervenons auprès des particuliers et des professionnels pour tous types de projets : installations électriques neuves, rénovations, mises aux normes, dépannages, ainsi que tous travaux de plâtrerie et d'isolation.</p>
            <p>Que ce soit pour un petit dépannage ou un projet de grande envergure, TSE OUEST s'engage à vous fournir un service professionnel, rapide et de qualité, dans le respect des normes en vigueur.</p>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal className="relative">
            <div className="brackets always rounded-3xl overflow-hidden shadow-[0_30px_60px_rgba(11,17,32,0.15)]">
              <img src="/assets/commercial4-dream-team.webp" alt="Équipe TSE OUEST" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
          <div className="mt-12 relative pl-8 border-l border-slate-300">
            {milestones.map((m, i) => (
              <Reveal key={m.year} delay={i * 120} className="relative pb-10 last:pb-0">
                <span className="absolute -left-[41px] top-1 w-4 h-4 rounded-full bg-energy ring-4 ring-[#F8FAFC] shadow-[0_0_12px_#FF5E14]" />
                <p className="font-mono text-sm font-bold text-energy">{m.year}</p>
                <p className="text-slate-700 mt-1">{m.event}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <section className="relative bg-obsidian text-white py-24 lg:py-32 overflow-hidden noise" data-testid="about-values">
      <div className="absolute inset-0 dot-matrix opacity-30" />
      <div className="glow-orb w-[400px] h-[400px] bg-signal/10 -bottom-40 -left-20" />
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeader dark eyebrow="Nos valeurs" title="Ce qui guide chacun de nos chantiers" />
        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 80}>
              <TechCard dark className="h-full group">
                <IconBox icon={v.icon} tone={i % 2 ? 'signal' : 'energy'} />
                <h3 className="mt-6 font-display text-xl font-bold">{v.title}</h3>
                <p className="text-sm text-slate-400 mt-3 leading-relaxed">{v.description}</p>
              </TechCard>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-white text-ink py-24 lg:py-32" data-testid="about-team">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-14 items-center">
          <div className="lg:col-span-5">
            <SectionHeader eyebrow="Notre équipe" title="Des passionnés, formés aux dernières techniques." subtitle="TSE OUEST, c'est avant tout une équipe à l'écoute de vos besoins, qui place la dimension humaine au cœur de chaque projet." />
            <div className="mt-10 grid grid-cols-3 gap-6">
              <Stat value={companyInfo.experience} suffix="+" label="Années" dark={false} />
              <Stat value={1000} suffix="+" label="Projets" dark={false} />
              <Stat value={100} suffix="%" label="Satisfaits" dark={false} />
            </div>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-3 gap-5">
            {certs.map((c, i) => (
              <Reveal key={c.label} delay={i * 100}>
                <TechCard className="h-full group">
                  <IconBox icon={c.icon} tone="ink" />
                  <p className="mt-6 font-mono text-xs uppercase tracking-[0.2em] text-energy">Certification</p>
                  <h3 className="mt-2 font-display text-lg font-bold">{c.label}</h3>
                  <p className="text-sm text-slate-600 mt-2">{c.text}</p>
                </TechCard>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <CTA title="Travaillons ensemble" text="Parlez-nous de votre projet : nous vous répondons sous 24h avec un devis gratuit." />
  </div>
);

export default About;
